import type { ReactNode } from 'react';
import { useEffect } from 'react';
import { Provider } from 'react-redux';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import { I18nextProvider } from 'react-i18next';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import { store } from '@core/store/store';
import { theme } from '@shared/theme';
import type { AppDispatch } from '@core/store/store';
import { initializeAuth } from '@core/auth/store/authSlice';
import { setPermissions } from '@core/auth/store/authSlice';
import { selectIsAuthenticated, selectIsInitialized } from '@core/auth/store/authSelectors';
import { i18n } from '@app/i18n';
import { useGetUserDetailsQuery } from '@modules/users/api/usersApi';
import { DatePickerLocaleProvider } from './DatePickerLocaleProvider';

interface AppProvidersProps {
  children: ReactNode;
}

export const AppProviders = ({ children }: AppProvidersProps) => (
  <Provider store={store}>
    <I18nextProvider i18n={i18n}>
      <BrowserRouter>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <DatePickerLocaleProvider>
            <AuthBootstrapper>
              <PermissionsBootstrapper>{children}</PermissionsBootstrapper>
            </AuthBootstrapper>
          </DatePickerLocaleProvider>
        </ThemeProvider>
      </BrowserRouter>
    </I18nextProvider>
  </Provider>
);

const AuthBootstrapper = ({ children }: { children: ReactNode }) => {
  const dispatch = useDispatch<AppDispatch>();

  useEffect(() => {
    void dispatch(initializeAuth());
  }, [dispatch]);

  return <>{children}</>;
};

/**
 * Fetches roles+permissions from ERP after Keycloak login.
 *
 * This is needed when permissions are NOT stored in Keycloak token claims,
 * but in ERP DB (Users module).
 */
const PermissionsBootstrapper = ({ children }: { children: ReactNode }) => {
  const dispatch = useDispatch<AppDispatch>();
  const isInitialized = useSelector(selectIsInitialized);
  const isAuthenticated = useSelector(selectIsAuthenticated);

  const { data } = useGetUserDetailsQuery(undefined, {
    skip: !isInitialized || !isAuthenticated,
  });

  useEffect(() => {
    if (data?.permissions) {
      dispatch(setPermissions(data.permissions));
    }
  }, [data, dispatch]);

  return <>{children}</>;
};
